import { css } from '@emotion/react';
import { MarkdownBody } from '@inflearn/ds-markdown-body';
import {
  Anchor,
  Box,
  Button,
  COLOR_PALETTE,
  Divider,
  Flex,
  Icon,
  Stack,
  Text,
} from '@inflearn/ds-react-next';
import { faCirclePlay } from '@inflearn/pro-solid-svg-icons';
import { useTranslation } from 'next-i18next';
import { Fragment } from 'react';

import type {
  NoteWithUnitsNoteDetailResponse,
  NoteWithUnitsSectionResponse,
  NoteWithUnitsUnitResponse,
} from '../../../../../resources/@types/uccSchema';
import { INFLEARN_URL } from '../../../../constants/urls';
import useRouterQueryId from '../../../../hooks/useRouterQueryId';
import { secondsToTimestamp } from '../../../../utils/time';
import useNoteIdMetadataQuery from '../hooks/queries/useNoteIdMetadataQuery';
import useNoteIdWithUnitsQuery from '../hooks/queries/useNoteIdWithUnitsQuery';
import useDeleteNote from '../hooks/useDeleteNote';
import useEditNote from '../hooks/useEditNote';
import { generateSectionId } from '../utils/toc';
import NoteEditor from './NoteEditor';

export default function NoteSection() {
  const courseNoteId = useRouterQueryId();

  const { data: noteData } = useNoteIdWithUnitsQuery({
    id: courseNoteId,
  });
  const sections = noteData?.sections;

  if (!sections) {
    return null;
  }

  return (
    <>
      {sections.map((section, index) => (
        <Fragment key={section.id}>
          <CourseSection section={section} />
          {index < sections.length - 1 && <Divider my={40} />}
        </Fragment>
      ))}
    </>
  );
}

function CourseSection({ section }: { section: NoteWithUnitsSectionResponse }) {
  return (
    <Stack gap={4} id={generateSectionId(section.id)}>
      <Text size="sm" c="gray.6">
        {section.title}
      </Text>
      <Stack gap={32}>
        {section.units.map((unit) => (
          <Unit key={unit.id} unit={unit} />
        ))}
      </Stack>
    </Stack>
  );
}

function Unit({ unit }: { unit: NoteWithUnitsUnitResponse }) {
  return (
    <Stack gap={4}>
      <Text size="lg" c="gray.9" fw={700}>
        {unit.title}
      </Text>
      <Stack gap={12}>
        {unit.notes.map((note) => (
          <Note key={note.id} note={note} unitId={unit.id} />
        ))}
      </Stack>
    </Stack>
  );
}

function Note({ note, unitId }: { note: NoteWithUnitsNoteDetailResponse; unitId: number }) {
  const { t } = useTranslation();
  const courseNoteId = useRouterQueryId();

  const { data: metadata } = useNoteIdMetadataQuery(courseNoteId);
  const { data: noteData } = useNoteIdWithUnitsQuery({ id: courseNoteId });
  const course = noteData?.course;

  const { handleClickDelete, isDeleting } = useDeleteNote(String(note.id), courseNoteId);
  const { noteBody, isEditMode, handleClickEdit, handleCancelEdit, handleSubmitEdit } = useEditNote(
    String(note.id),
    note.body,
    courseNoteId,
  );

  return (
    <Stack gap={4}>
      <Flex
        align="center"
        styles={{
          root: { minHeight: 22 },
        }}
      >
        {/* 타임스탬프 */}
        {course && !!unitId && (
          <Anchor
            left="fa-house"
            href={`${INFLEARN_URL.COURSE_UNIT({ slug: course.slug, unitId })}?time=${note.unitCurrentTime}&tab=note`}
            target="_blank"
          >
            <Flex align="center" gap={4}>
              <Icon icon={faCirclePlay} size="sm" />
              <Text size="sm" fw={500}>
                {secondsToTimestamp(note.unitCurrentTime)}
              </Text>
            </Flex>
          </Anchor>
        )}
        {/* 수정, 삭제 버튼 */}
        {metadata?.isWritable && !isEditMode && (
          <Flex gap={8} ml={'auto'}>
            <Button
              variant="subtle"
              color="gray"
              size="compact-xs"
              radius="md"
              disabled={isDeleting}
              onClick={handleClickEdit}
            >
              {t('수정')}
            </Button>
            <Button
              variant="subtle"
              color="gray"
              size="compact-xs"
              radius="md"
              disabled={isDeleting}
              onClick={handleClickDelete}
            >
              {t('삭제')}
            </Button>
          </Flex>
        )}
      </Flex>
      {/* 노트 본문 */}
      {!isEditMode && (
        <Box
          w="100%"
          p={20}
          css={{
            backgroundColor: COLOR_PALETTE.gray[0],
            borderRadius: 8,
          }}
        >
          <MarkdownBody
            html={noteBody}
            styleWrapper={css({
              backgroundColor: 'inherit',
              img: {
                height: 'auto',
              },
            })}
          />
        </Box>
      )}
      {/* 에디터 */}
      {isEditMode && (
        <Box mb={16}>
          <NoteEditor
            initialValue={noteBody}
            onCancel={handleCancelEdit}
            onSubmit={handleSubmitEdit}
            noteId={note.id}
          />
        </Box>
      )}
    </Stack>
  );
}
